import { useEffect, useState } from "react";
import axios from "axios";
import { Label } from "@/components/ui/label";
import { AlertTriangle, CalendarClock, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (v) => (parseFloat(v) || 0).toFixed(2);
const fmtDate = (d) => (d ? new Date(`${d}T00:00:00`).toLocaleDateString("en-ZA", { day: "2-digit", month: "short", year: "numeric" }) : "-");

const FILTERS = [
  { key: "overdue", label: "Overdue" },
  { key: "week", label: "Due this week" },
  { key: "all", label: "All unpaid" },
];

const statusBadge = (inv) => {
  if (inv.status === "overdue") return <span className="rounded bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700">Overdue {-inv.days_to_due}d</span>;
  if (inv.status === "due_today") return <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-800">Due today</span>;
  return <span className={`rounded px-2 py-0.5 text-xs font-semibold ${inv.status === "due_soon" ? "bg-amber-50 text-amber-700" : "bg-gray-100 text-gray-600"}`}>Due in {inv.days_to_due}d</span>;
};

export const useInvoicesDue = (customerId, refreshKey) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!customerId) return;
    axios.get(`${API}/customers/${customerId}/invoices-due`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [customerId, refreshKey]);
  return data;
};

export const AgingBoxes = ({ data }) => (
  <div className="grid grid-cols-5 gap-2" data-testid="journal-aging-boxes">
    {[["current", "Current"], ["days_30", "30 Days"], ["days_60", "60 Days"], ["days_90", "90 Days"], ["days_120", "120+ Days"]].map(([k, label]) => (
      <div key={k}>
        <Label className="text-xs text-gray-500">{label}</Label>
        <div className={`p-2 rounded text-sm font-mono text-center ${k !== "current" && (data?.aging?.[k] || 0) > 0 ? "bg-red-50 text-red-700" : "bg-gray-100"}`} data-testid={`journal-aging-${k}`}>
          {fmt(data?.aging?.[k])}
        </div>
      </div>
    ))}
  </div>
);

export const InvoicesDuePanel = ({ data }) => {
  const [filter, setFilter] = useState("overdue");
  if (!data) return <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" />Loading invoices due…</div>;
  const rows = data.invoices.filter((i) => filter === "all" || (filter === "overdue" ? i.status === "overdue" : i.status === "due_today" || i.status === "due_soon"));
  const t = data.totals;
  return (
    <div className="rounded-lg border" data-testid="invoices-due-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 border-b bg-gray-50 px-4 py-2">
        <div className="flex items-center gap-2 font-semibold"><CalendarClock className="h-4 w-4" />Invoices Due <span className="text-xs font-normal text-gray-500">(terms: {data.payment_terms})</span></div>
        <div className="flex flex-wrap gap-3 text-xs">
          <span className="font-semibold text-red-700" data-testid="invoices-due-overdue-total"><AlertTriangle className="mr-1 inline h-3 w-3" />Overdue R {fmt(t.overdue)}</span>
          <span className="font-semibold text-amber-700" data-testid="invoices-due-week-total">This week R {fmt(t.due_this_week)}</span>
          <span className="text-gray-600">Not yet due R {fmt(t.not_yet_due)}</span>
        </div>
      </div>
      <div className="flex gap-2 px-4 pt-3">
        {FILTERS.map((f) => (
          <button key={f.key} type="button" onClick={() => setFilter(f.key)} data-testid={`invoices-due-filter-${f.key}`}
            className={`rounded-full border px-3 py-1 text-xs transition-colors ${filter === f.key ? "border-gray-800 bg-gray-800 text-white" : "bg-white hover:bg-gray-100"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="max-h-64 overflow-auto p-4 pt-2">
        {rows.length === 0 ? (
          <div className="py-4 text-center text-sm text-gray-500" data-testid="invoices-due-empty">Nothing here</div>
        ) : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-gray-500"><th className="py-1">Invoice</th><th>Date</th><th>Due</th><th className="text-right">Invoice Total</th><th className="text-right">Still Owing</th><th className="text-right">Status</th></tr></thead>
            <tbody>
              {rows.map((i) => (
                <tr key={`${i.inv_no}-${i.date}`} className="border-t" data-testid={`invoices-due-row-${i.inv_no}`}>
                  <td className="py-1.5 font-mono">{i.inv_no}</td>
                  <td>{fmtDate(i.date)}</td>
                  <td>{fmtDate(i.due_date)}</td>
                  <td className="text-right font-mono">{fmt(i.inv_total)}</td>
                  <td className="text-right font-mono font-semibold">{fmt(i.outstanding)}</td>
                  <td className="text-right">{statusBadge(i)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};
